import { useEffect, useRef, useState } from 'react';
import type { Unsubscribe } from 'firebase/firestore';

import { env } from './env.js';
import { logger } from './logger.js';

/**
 * Hook for Firestore real-time subscriptions.
 * Tracks subscribe/unsubscribe pairs; in dev, asserts that every snapshot
 * registered during a mount is torn down at unmount.
 *
 * Use this INSTEAD of calling onSnapshot directly. The lint rule
 * `no-restricted-imports` blocks `firebase/firestore` outside `lib/repositories/`,
 * so this hook consumes a subscribe-function that the repo returns.
 */
export type Subscribe<T> = (cb: (value: T) => void) => Unsubscribe;

export interface TrackedSnapshotState<T> {
  readonly data: T | null;
  readonly loading: boolean;
  readonly error: Error | null;
}

const liveCount = { value: 0 };

export function useTrackedSnapshot<T>(
  key: readonly unknown[],
  subscribe: Subscribe<T> | null,
): TrackedSnapshotState<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const keyRef = useRef(JSON.stringify(key));
  keyRef.current = JSON.stringify(key);

  useEffect(() => {
    if (!subscribe) return undefined;

    let cancelled = false;
    liveCount.value += 1;
    if (env.mode !== 'production') {
      logger.debug('snapshot:subscribe', { key: keyRef.current, live: liveCount.value });
    }

    let unsub: Unsubscribe | null = null;
    try {
      unsub = subscribe((value) => {
        if (cancelled) return;
        setData(value);
        setLoading(false);
        setError(null);
      });
    } catch (e) {
      if (!cancelled) {
        setError(e instanceof Error ? e : new Error(String(e)));
        setLoading(false);
      }
    }

    return () => {
      cancelled = true;
      if (unsub) unsub();
      liveCount.value -= 1;
      if (env.mode !== 'production') {
        logger.debug('snapshot:unsubscribe', { key: keyRef.current, live: liveCount.value });
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keyRef.current, subscribe]);

  return { data, loading, error };
}

export function _liveSnapshotCount(): number {
  return liveCount.value;
}
